import { FileActionButton } from "../file-action/file-action-controls";


// src/components/RawMaterials/RawMaterialPurchaseMain.tsx

import React, { useCallback, useEffect, useState, useRef } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import RawMaterialsSearch from "./RawMaterial-Search";
import { Toast } from "primereact/toast";
import { rawMaterialPurchaseExport } from "./RawMaterialPurchaseExport";

import RawMaterialPurchaseViewTable from "./RawMaterialPurchaseViewTable";
import RawMaterialPurchaseForm from "./RawMaterialspurchase"; // your existing form file
import RawMaterialService from "../../services/RawMaterials/RawMaterialService";
import { RawMaterialPurchaseListDTO } from "../../models/RawMaterials/RawMaterial-model";

const RawMaterialPurchaseMain: React.FC = () => {
  const [rows, setRows] = useState<RawMaterialPurchaseListDTO[]>([]);
  const [qcStatuses, setQcStatuses] = useState<any[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string | undefined>("purchaseDate");
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(-1);
  const [showDialog, setShowDialog] = useState(false);
  const toast = useRef<Toast>(null);

  const [filters, setFilters] = useState<any>({});

  const fetchRows = useCallback(async () => {
    const payload: any = {
      pageNumber,
      pageSize,
      sortColumn: sortField,
      sortDirection: sortOrder === 1 ? "asc" : "desc",
      ...filters,
    };

    const { rawMaterialPurchases, totalRecords, qcStatuses } =
      await RawMaterialService.getRawMaterialPurchases(payload);

    setRows(rawMaterialPurchases ?? []);
    setTotalRecords(totalRecords ?? 0);
    setQcStatuses(qcStatuses ?? []);
  }, [pageNumber, pageSize, sortField, sortOrder, filters]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // pos list and options are loaded by RawMaterialsSearch; we only need filters here

  const handlePageChange = (event: any) => {
    setPageNumber((event.page ?? 0) + 1);
    setPageSize(event.rows ?? pageSize);
  };

  const handleSortChange = (field: string, order: 1 | -1) => {
    setSortField(field);
    setSortOrder(order);
    setPageNumber(1);
  };

  // For now, just refetch from the server after an update
  const handleUpdateRow = (updated: RawMaterialPurchaseListDTO) => {
    void fetchRows();
  };

  const handlePurchaseSuccess = () => {
    setShowDialog(false);
    void fetchRows();
  };

  const header = (
    <div className="flex align-items-center justify-content-between mb-2">
      <h5 className="ml-1 mb-0">Purchases</h5>
      <FileActionButton action="export"
        label="Export"
        icon="pi pi-file-excel"
        className="p-button-success"
        onClick={async () => {
          try {
            const payload = { pageNumber: 1, pageSize: totalRecords || 1000, ...filters };
            await rawMaterialPurchaseExport(payload as any);
          } catch (err) {
            console.error('Export failed', err);
            toast.current?.show({ severity: 'error', summary: 'Export failed', detail: String(err) });
          }
        }}
      />
    </div>
  );

  return (
    <div className="layout-content mt-3 mb-3 grid grid-table-mobile">
      <Toast ref={toast} />

      <div className="col-12">
        {/* Header card (movement-style) */}
        <div className="card p-4 w-full" style={{ backgroundColor: '#e8f5e9', borderRadius: '12px' }}>
          <div className="flex align-items-center justify-content-between mb-4" style={{ backgroundImage: 'linear-gradient(to right, white, rgb(232, 245, 233))', padding: '16px', borderRadius: '8px', marginLeft: '-16px', marginRight: '-16px', marginTop: '-16px', paddingLeft: '16px', paddingRight: '16px' }}>
            <div className="flex align-items-center gap-3">
              <div style={{ backgroundColor: 'rgb(76, 175, 80)', borderRadius: '10px', padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-box" style={{ color: 'white', fontSize: '1.6rem' }}></i>
              </div>
              <div>
                <h3 className="m-0 mb-1" style={{ color: '#2d3748', fontWeight: '700' }}>Purchases</h3>
                <p className="m-0" style={{ color: '#6b7280', fontSize: '0.95rem' }}>View and manage raw material purchases</p>
              </div>
            </div>

            <div>
              <Button label="Purchase" icon="pi pi-plus" className="p-button-success p-button-rounded" style={{ padding: '0.75rem 1.25rem', fontSize: '1rem' }} onClick={() => setShowDialog(true)} />
            </div>
          </div>

          <RawMaterialsSearch
            onSearch={(searchFilters) => {
              setFilters(searchFilters);
              setPageNumber(1);
            }}
            onReset={() => {
              setFilters({});
              setPageNumber(1);
            }}
            totalRecords={totalRecords}
          />
        </div>

        <div className="card p-2 mt-3">
          {header}
          <RawMaterialPurchaseViewTable
            data={rows}
            pageNumber={pageNumber}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}
            sortField={sortField}
            sortOrder={sortOrder}
            isEmptyState={totalRecords === 0}
            qcStatuses={qcStatuses}
            onUpdate={handleUpdateRow}
            onRefresh={fetchRows}
            hideQcColumns={true}
          />
        </div>
      </div>

      <Dialog
        header="New Purchase"
        visible={showDialog}
        modal
        style={{ width: "60vw", maxWidth: "900px" }}
        onHide={() => setShowDialog(false)}
      >
        <RawMaterialPurchaseForm onSuccess={handlePurchaseSuccess} />
      </Dialog>
    </div>
  );
};

export default RawMaterialPurchaseMain;
